import { useCallback, useEffect, useRef, useState } from 'react';

import { clampChatOffset } from './chatUiPolicy';

const POINTER_BUTTON_PRIMARY = 0;
const KEYBOARD_STEP = 10;
const KEYBOARD_LARGE_STEP = 40;

/** PC 채팅 창을 제목 영역에서만 이동시키는 포인터·키보드 훅입니다. */
export default function useDraggableChatWindow(roomRef, t, layoutKey) {
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const pointerSessionRef = useRef(null);
  const previousLayoutKeyRef = useRef(layoutKey);

  /** 현재 DOM 위치에서 transform 이전의 원점과 창 크기를 계산합니다. */
  const measure = useCallback(
    (currentPosition) => {
      const rect = roomRef.current?.getBoundingClientRect();
      if (!rect) return null;
      return {
        origin: {
          left: rect.left - currentPosition.x,
          top: rect.top - currentPosition.y,
        },
        size: { width: rect.width, height: rect.height },
      };
    },
    [roomRef],
  );

  const clamp = useCallback(
    (requested, currentPosition, measured) => {
      const bounds = measured || measure(currentPosition);
      if (!bounds) return requested;
      return clampChatOffset(requested, bounds.origin, bounds.size, {
        width: window.innerWidth,
        height: window.innerHeight,
      });
    },
    [measure],
  );

  const handlePointerDown = (event) => {
    if (event.button !== POINTER_BUTTON_PRIMARY) return;
    const measured = measure(position);
    if (!measured) return;

    pointerSessionRef.current = {
      pointerId: event.pointerId,
      clientX: event.clientX,
      clientY: event.clientY,
      position,
      measured,
    };
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };

  const handlePointerMove = (event) => {
    const session = pointerSessionRef.current;
    if (!session || session.pointerId !== event.pointerId) return;

    const requested = {
      x: session.position.x + event.clientX - session.clientX,
      y: session.position.y + event.clientY - session.clientY,
    };
    setPosition(clamp(requested, session.position, session.measured));
  };

  const endPointerSession = (event) => {
    const session = pointerSessionRef.current;
    if (!session || session.pointerId !== event.pointerId) return;
    event.currentTarget.releasePointerCapture?.(event.pointerId);
    pointerSessionRef.current = null;
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Home') {
      event.preventDefault();
      setPosition({ x: 0, y: 0 });
      return;
    }

    const step = event.shiftKey ? KEYBOARD_LARGE_STEP : KEYBOARD_STEP;
    const deltaByKey = {
      ArrowLeft: { x: -step, y: 0 },
      ArrowRight: { x: step, y: 0 },
      ArrowUp: { x: 0, y: -step },
      ArrowDown: { x: 0, y: step },
    };
    const delta = deltaByKey[event.key];
    if (!delta) return;

    event.preventDefault();
    setPosition((current) =>
      clamp({ x: current.x + delta.x, y: current.y + delta.y }, current),
    );
  };

  useEffect(() => {
    const keepInsideViewport = () => {
      setPosition((current) => clamp(current, current));
    };
    window.addEventListener('resize', keepInsideViewport);
    return () => window.removeEventListener('resize', keepInsideViewport);
  }, [clamp]);

  useEffect(() => {
    if (previousLayoutKeyRef.current === layoutKey) return undefined;
    previousLayoutKeyRef.current = layoutKey;

    // 일반/축소 전환의 새 크기가 DOM에 반영된 다음 현재 좌표를 다시 경계 안에 둡니다.
    const frameId = window.requestAnimationFrame(() => {
      setPosition((current) => clamp(current, current));
    });
    return () => window.cancelAnimationFrame(frameId);
  }, [clamp, layoutKey]);

  return {
    position,
    transformStyle: {
      transform: `translate3d(${position.x}px, ${position.y}px, 0)`,
    },
    dragHandleProps: {
      role: 'button',
      tabIndex: 0,
      'aria-label': t('채팅 창 이동 영역'),
      'aria-keyshortcuts': 'ArrowUp ArrowDown ArrowLeft ArrowRight Shift+ArrowUp Shift+ArrowDown Shift+ArrowLeft Shift+ArrowRight Home',
      onPointerDown: handlePointerDown,
      onPointerMove: handlePointerMove,
      onPointerUp: endPointerSession,
      onPointerCancel: endPointerSession,
      onKeyDown: handleKeyDown,
    },
    statusText: t('채팅 창 위치', {
      x: Math.round(position.x),
      y: Math.round(position.y),
    }),
  };
}
